import React, { useState, useEffect, ChangeEvent } from 'react';
import { Button, Typography } from '@navi/web-ui/lib/primitives';
import { RadioGroup } from '@navi/web-ui/lib/components';
import { StrategyType, AllocationDpdFilter, EndOfNavigationModalProps } from '../types';
import { DPD_OPTIONS, STRATEGY_OPTIONS } from '../constants';
import Modal from '@cp/src/components/Modal/Modal';

const EndOfNavigationModal: React.FC<EndOfNavigationModalProps> = ({
  isOpen,
  currentStrategy,
  currentDpdFilter,
  cohortLabel,
  onStartAgain,
  onChangeStrategy,
  onClose
}) => {
  const [selectedDpd, setSelectedDpd] = useState<AllocationDpdFilter>(
    currentDpdFilter || AllocationDpdFilter.OVERALL
  );
  const [selectedStrategy, setSelectedStrategy] = useState<StrategyType>(
    currentStrategy || StrategyType.NON_CONTACTABLE
  );
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setSelectedDpd(currentDpdFilter || AllocationDpdFilter.OVERALL);
      setSelectedStrategy(currentStrategy || StrategyType.NON_CONTACTABLE);
      setHasChanges(false);
    }
  }, [isOpen, currentDpdFilter, currentStrategy]);

  const handleDpdChange = (value: AllocationDpdFilter) => {
    setSelectedDpd(value);
    setHasChanges(value !== currentDpdFilter || selectedStrategy !== currentStrategy);
  };

  const handleStrategyChange = (value: StrategyType) => {
    setSelectedStrategy(value);
    setHasChanges(selectedDpd !== currentDpdFilter || value !== currentStrategy);
  };

  const handleStartAgain = () => {
    onStartAgain(selectedDpd, selectedStrategy);
  };

  const handleChangeStrategy = () => {
    onChangeStrategy(selectedDpd, selectedStrategy);
  };

  const primaryButtonText = hasChanges ? 'Change strategy' : 'Start again';
  const primaryButtonAction = hasChanges ? handleChangeStrategy : handleStartAgain;

  const headerContent = (
    <Typography variant="p3" color="var(--grayscale-2)">
      You have successfully attempted cases in the cohort{' '}
      <Typography variant="h5" color="var(--grayscale-1)" className="font-bold inline">
        {cohortLabel}{' '}
      </Typography>
      .Do you want to start same strategy again or try different strategy?
    </Typography>
  );

  return (
    <Modal
      visible={isOpen}
      onClose={onClose}
      header={headerContent}
      closeOnOutsideClick
      contentWrapperClass="max-w-[555px] w-full"
      showCloseIcon={false}
    >
      <div className="border border-solid border-[var(--greyscale-light)] rounded-lg mb-4">
        <div className="grid grid-cols-2">
          <div className="p-4 border-0 border-r border-solid border-[var(--greyscale-light)]">
            <RadioGroup
              direction="column"
              name="dpd-filter-end"
              value={selectedDpd}
              options={DPD_OPTIONS}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                handleDpdChange(e.target.value as AllocationDpdFilter)
              }
              className="gap-3"
            />
          </div>

          <div className="p-4">
            <RadioGroup
              direction="column"
              name="strategy-filter-end"
              value={selectedStrategy}
              options={STRATEGY_OPTIONS}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                handleStrategyChange(e.target.value as StrategyType)
              }
              className="gap-3"
            />
          </div>
        </div>
      </div>

      <div className="flex gap-3">
        <Button variant="secondary" className="flex-1 h-[44px]" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" className="flex-1 h-[44px]" onClick={primaryButtonAction}>
          {primaryButtonText}
        </Button>
      </div>
    </Modal>
  );
};

export default EndOfNavigationModal;
